import { useState, useMemo } from "react";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, Check, Globe, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EmbedCodePage() {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const [copied, setCopied] = useState("");
  const [refName, setRefName] = useState("");

  const snippets = useMemo(() => {
    const refAttr = refName ? ` data-ref-name="${refName}"` : "";
    return {
      button: `<!-- UKC Legal Quote Widget — floating button (bottom-right) -->
<script src="${baseUrl}/embed.js"
        data-base="${baseUrl}"
        data-mode="button"${refAttr}
        defer></script>`,
      trigger: `<!-- UKC Legal Quote Widget — opens when YOUR button is clicked -->
<a href="#" data-ukc-quote
   style="display:inline-block;background:#100f5d;color:#fff;padding:12px 24px;
          border-radius:8px;text-decoration:none;font-weight:600;">
  Get an Instant Quote
</a>
<script src="${baseUrl}/embed.js"
        data-base="${baseUrl}"
        data-mode="trigger"${refAttr}
        defer></script>`,
      auto: `<!-- UKC Legal Quote Widget — opens immediately on page load -->
<script src="${baseUrl}/embed.js"
        data-base="${baseUrl}"
        data-mode="auto"${refAttr}
        defer></script>`,
      analytics: `<!-- Hook into the quote-generated event for GA / Meta Pixel etc. -->
<script>
document.addEventListener('ukc:quote-generated', function(e){
  console.log('Quote generated:', e.detail);
  // Example: send to Google Analytics
  if (window.gtag) gtag('event', 'generate_lead', {
    event_category: 'conveyancing_quote',
    value: e.detail.total,
    quote_id: e.detail.quote_id,
  });
});
</script>`,
    };
  }, [baseUrl, refName]);

  const copy = (key) => {
    navigator.clipboard.writeText(snippets[key]);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="embed-code-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: "#100f5d" }}>
            Embed on your website
          </h1>
          <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>
            Drop a single &lt;script&gt; tag onto ukclegal.co.uk and visitors can get an instant quote without leaving the page. Submitted quotes flow straight into your dashboard.
          </p>
        </div>

        {/* Optional referrer pre-fill */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm mb-6">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Pre-fill referrer (optional)</label>
          <input
            data-testid="embed-ref-name-input"
            type="text"
            value={refName}
            onChange={(e) => setRefName(e.target.value)}
            placeholder="e.g. Smith & Co Estate Agents"
            className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-[#100f5d] focus:ring-2 focus:ring-[#100f5d]/20"
          />
          <p className="text-[11px] text-slate-400 mt-1.5">If you embed this on a partner agent's site, set their name here so the quote auto-attributes the referral.</p>
        </div>

        <Tabs defaultValue="button" className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-xl mb-4">
            <TabsTrigger value="button" data-testid="snippet-tab-button">Floating button</TabsTrigger>
            <TabsTrigger value="trigger" data-testid="snippet-tab-trigger">Custom button</TabsTrigger>
            <TabsTrigger value="auto" data-testid="snippet-tab-auto">Auto-open</TabsTrigger>
          </TabsList>

          {[
            { key: "button", label: "Floating button (recommended)", desc: "Adds a navy 'Get an Instant Quote' pill to the bottom-right of every page on ukclegal.co.uk. Visitors click → branded quote modal opens." },
            { key: "trigger", label: "Wire up your own button", desc: "Already have a 'Quote' button on your homepage? Add `data-ukc-quote` to it and we'll handle the rest. Snippet below shows a sample button you can restyle." },
            { key: "auto", label: "Auto-open on landing", desc: "Best for dedicated landing pages (e.g. /quote on your website) — opens the modal as soon as the page loads." },
          ].map((opt) => (
            <TabsContent key={opt.key} value={opt.key}>
              <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{opt.label}</h3>
                <p className="text-xs text-slate-500 mt-1 mb-4">{opt.desc}</p>
                <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-md overflow-x-auto" data-testid={`snippet-code-${opt.key}`}>
                  <code>{snippets[opt.key]}</code>
                </pre>
                <Button onClick={() => copy(opt.key)} className="mt-3 text-white" style={{ background: "#100f5d" }} data-testid={`copy-snippet-${opt.key}`}>
                  {copied === opt.key ? <Check weight="bold" className="w-4 h-4 mr-2" /> : <Copy weight="bold" className="w-4 h-4 mr-2" />}
                  {copied === opt.key ? "Copied!" : "Copy snippet"}
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Analytics hook */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm mt-6">
          <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Track quotes in Google Analytics (optional)</h3>
          <p className="text-xs text-slate-500 mb-3">Drop this <strong>after</strong> the widget script to send a `generate_lead` event with the quote total whenever someone completes the form.</p>
          <pre className="bg-slate-900 text-slate-100 text-xs p-4 rounded-md overflow-x-auto" data-testid="snippet-code-analytics">
            <code>{snippets.analytics}</code>
          </pre>
          <Button onClick={() => copy("analytics")} variant="outline" className="mt-3" data-testid="copy-snippet-analytics">
            {copied === "analytics" ? <Check weight="bold" className="w-4 h-4 mr-2" /> : <Copy weight="bold" className="w-4 h-4 mr-2" />}
            {copied === "analytics" ? "Copied!" : "Copy snippet"}
          </Button>
        </div>

        {/* Live preview link */}
        <div className="rounded-lg p-5 mt-6 flex items-center justify-between" style={{ background: "rgba(16,15,93,0.06)", border: "1px solid rgba(16,15,93,0.15)" }}>
          <div className="flex items-center gap-3">
            <Globe weight="fill" className="w-5 h-5" style={{ color: "#100f5d" }} />
            <div>
              <div className="text-sm font-semibold text-slate-900">Live test page</div>
              <div className="text-xs text-slate-500">A sample HTML page using the floating-button snippet — check exactly what your visitors will see.</div>
            </div>
          </div>
          <a href={`${baseUrl}/embed-test.html`} target="_blank" rel="noopener noreferrer" data-testid="embed-test-link">
            <Button variant="outline" className="border-[#100f5d] text-[#100f5d] hover:bg-[#100f5d]/5">
              Open preview <ArrowSquareOut weight="bold" className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </a>
        </div>
      </main>
    </div>
  );
}
